import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { useEsCliente } from "../../../../shared/hooks/useTenantTipo";
import RowActionsMenu from "../../../../shared/components/RowActionsMenu";

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

/** "2025-01-15" → "15/01/2025" */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function getMesPeriodo(invoice) {
  const concepto = (invoice.concepto || "").toLowerCase();
  const mesIdx = MESES.findIndex((m) => concepto.includes(m.toLowerCase()));
  if (mesIdx !== -1) return MESES[mesIdx];
  if (invoice.fecha_emision?.length >= 7) {
    const mesNum = parseInt(invoice.fecha_emision.split("-")[1], 10) - 1;
    return MESES[mesNum] ?? "";
  }
  return "";
}

export default function InvoiceRow({
  invoice,
  onEdit,
  onDelete,
  onEmail,
  onEmitirNota,
  selected = false,
  onToggleSelect,
}) {
  const { t } = useTranslation();
  const esCliente = useEsCliente();
  // Emitir notas (crédito/débito): permitido a contador, administrador del
  // condominio (rol USUARIO) y superusuarios; no a RESIDENTE.
  const _rol = localStorage.getItem("rol") || "USUARIO";
  const _isSuper = localStorage.getItem("is_superuser") === "true";
  const puedeEmitirNota = _isSuper || _rol !== "RESIDENTE";
  const rubro = invoice.cuenta_ingreso?.nombre ?? "";
  const esSaldoInicial = invoice.es_saldo_inicial && !invoice.cuenta_ingreso;
  const mes = getMesPeriodo(invoice);
  const esAgua = /agua/i.test(rubro);
  const consumo = invoice.consumo_agua;

  const id = invoice.id_factura ?? invoice.id;
  return (
    <tr style={{ background: selected ? "rgba(249,115,22,0.06)" : undefined }}>
      <td
        style={{ width: 36, paddingRight: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect?.(id)}
          style={{ cursor: "pointer", accentColor: "#f97316" }}
        />
      </td>
      <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>
        {invoice.numero_factura}
      </td>
      <td>{invoice.filial?.idFilial ?? ""}</td>
      {!esCliente && (
        <td
          title={invoice.nombre_propietario}
          style={{
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: invoice.nombre_propietario?.length > 25 ? "help" : "default",
          }}
        >
          {invoice.nombre_propietario}
        </td>
      )}

      {/* Rubro / Período — incluye medidas si es agua */}
      <td>
        {esSaldoInicial ? (
          <span style={{
            display: "inline-block", padding: "2px 8px", borderRadius: 4,
            background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.3)",
            color: "#a78bfa", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.04em",
          }}>
            AJUSTE
          </span>
        ) : (
          <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{rubro}</span>
        )}
        {mes && (
          <span
            style={{
              display: "block",
              fontSize: "0.75rem",
              color: "var(--text-secondary)",
              marginTop: 1,
            }}
          >
            {mes}
          </span>
        )}
        {esAgua &&
          invoice.medida_anterior != null &&
          invoice.medida_actual != null && (
            <span
              style={{
                display: "block",
                fontSize: "0.72rem",
                color: "#90CAF9",
                marginTop: 2,
              }}
            >
              <i className="fa-solid fa-droplet" style={{ marginRight: 3 }} />
              {Number(invoice.medida_anterior).toFixed(2)} →{" "}
              {Number(invoice.medida_actual).toFixed(2)} m³
              {consumo != null && (
                <span style={{ marginLeft: 4, color: "#81C784" }}>
                  ({Number(consumo).toFixed(2)} m³)
                </span>
              )}
            </span>
          )}
      </td>

      <td>{fmtDate(invoice.fecha_emision)}</td>
      <td>
        <div style={{ textAlign:"right", fontFamily:"Inter", fontWeight:300 }}>
          {invoice.moneda === "USD" ? (
            <>
              <span style={{ color: "#60a5fa" }}>${Number(invoice.monto).toFixed(2)}</span>
              {invoice.monto_colones && (
                <span style={{ display: "block", fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 1 }}>
                  ≈ ₡{formatCurrency(invoice.monto_colones)}
                </span>
              )}
            </>
          ) : (
            <>₡{formatCurrency(invoice.monto)}</>
          )}
        </div>
      </td>

      <td className="text-center">
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          <ActionBtn
            icon="fa-pencil"
            label={t("common.editar")}
            color="#f97316"
            hoverBg="rgba(249,115,22,0.15)"
            onClick={() => onEdit?.(invoice)}
          />
          <RowActionsMenu items={[
            { icon: "fa-envelope", label: t("common.correo"), color: "#60a5fa",
              onClick: () => onEmail?.(invoice) },
            puedeEmitirNota && { icon: "fa-file-pen", label: t("notas.emitir"), color: "#a78bfa",
              onClick: () => onEmitirNota?.(invoice) },
            { icon: "fa-trash", label: t("common.eliminar"), color: "#f87171",
              onClick: () => onDelete?.(invoice) },
          ]}/>
        </div>
      </td>
    </tr>
  );
}

function ActionBtn({ icon, label, color, hoverBg, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 30,
        height: 30,
        padding: 0,
        background: `${color}1a`,
        border: `1px solid ${color}4d`,
        borderRadius: 6,
        color,
        fontSize: "0.85rem",
        cursor: "pointer",
        transition: "background 0.15s, border-color 0.15s",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.background = hoverBg;
        e.currentTarget.style.borderColor = color;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = `${color}1a`;
        e.currentTarget.style.borderColor = `${color}4d`;
      }}
    >
      <i className={`fa-solid ${icon}`} />
    </button>
  );
}
